import { createFileRoute } from "@tanstack/react-router";

import { GuidePage, type GuideFaq, type GuideSection } from "@/components/seo/GuidePage";
import { guidePageHead } from "@/lib/guide-page-seo";

const PATH = "/guides/meta-ads-campaign-structure";
const HEADLINE = "How to structure Meta Ads campaigns";
const TITLE = "How to Structure Meta Ads Campaigns | Adkiro";
const DESCRIPTION =
  "How many campaigns and ad sets a Meta account should run, why fragmentation raises costs, when to consolidate, and how audience overlap quietly inflates cost per result.";

const KEY_POINTS = [
  "Structure exists to concentrate conversions, not to organise them for human convenience. Every extra ad set divides the signal the delivery system learns from.",
  "The practical constraint is the roughly 50 weekly optimisation events each ad set needs to stabilise. Divide expected weekly conversions by 50 to get the maximum number of ad sets the account can actually support.",
  "Two ad sets targeting overlapping audiences bid against each other in the same auction, which raises costs without adding reach.",
  "Broad targeting with strong creative generally outperforms narrow interest stacks, because it lets the delivery system find converters rather than guessing in advance.",
  "Separate campaigns are justified by genuinely different objectives, budgets or markets — not by wanting a tidy reporting view.",
];

const SECTIONS: GuideSection[] = [
  {
    heading: "Start from the volume the account can support",
    body: [
      "The most useful structural question is not how to organise campaigns but how many ad sets the account can afford to run. Each ad set needs approximately 50 optimisation events a week to leave the learning phase and deliver predictably. That gives a straightforward calculation: expected weekly conversions divided by 50 is the ceiling on ad sets.",
      "An account generating 100 purchases a week can support about two purchase-optimised ad sets. One generating 40 cannot support even one, and needs either a higher budget or an earlier optimisation event. Building eight ad sets in either case guarantees that none stabilises.",
      "This is why accounts often perform worse after being 'properly organised'. A structure that looks orderly in the interface — separate ad sets per interest, per age band, per placement — is usually one that has divided the account's conversions into pieces too small to learn from.",
    ],
    points: [
      "Weekly conversions ÷ 50 = the realistic maximum number of ad sets.",
      "Fewer ad sets with more volume each beats many ad sets with little.",
      "If the number comes out below one, the constraint is budget or the optimisation event.",
      "Reporting granularity is not a reason to split delivery.",
    ],
  },
  {
    heading: "Why fragmentation costs money",
    body: [
      "Splitting a budget across many ad sets does two distinct kinds of damage. The first is the learning problem: none of them accumulates enough events to optimise properly, so every one delivers less efficiently than a single consolidated set would.",
      "The second is auction overlap. When two ad sets target audiences that substantially intersect, they enter the same auctions for the same people. Meta's auction includes deduplication intended to limit an advertiser bidding against themselves, but overlapping ad sets still compete for a shared pool of impressions, and the practical result is higher costs and unstable delivery across both.",
      "Interest-based ad sets overlap far more than they appear to. Someone interested in running is very likely also interested in fitness, nutrition and sportswear, so four ad sets built on those interests are largely addressing the same people four times.",
    ],
  },
  {
    heading: "Broad targeting and where precision still matters",
    body: [
      "Meta's delivery system is generally better at identifying likely converters than an advertiser is at describing them in advance, provided it has enough conversion signal to work from. Broad targeting — minimal interest layering, wide age ranges, letting the system explore — gives it the room to do that, and tends to outperform tightly defined interest stacks in accounts with reasonable volume.",
      "This is not an argument that targeting never matters. Genuine constraints belong in targeting: countries you can ship to or service, age limits imposed by regulation or product suitability, languages you can support. These are exclusions of people who cannot become customers, which is different from guessing which of your possible customers are most likely to buy.",
      "Where audience precision has been replaced by creative precision, the creative carries the qualifying work. An ad that clearly states who the product is for filters the audience more effectively, and more cheaply, than an interest selection does.",
    ],
    points: [
      "Constraints belong in targeting: geography, language, regulated age limits.",
      "Predictions do not: interest stacks guessing who is most likely to buy.",
      "Broad targeting needs conversion volume to work; low-volume accounts benefit less.",
      "Creative that names its audience qualifies traffic better than narrow targeting.",
    ],
  },
  {
    heading: "When separate campaigns are justified",
    body: [
      "A separate campaign is warranted when something about it genuinely needs to be controlled independently. Different objectives are the clearest case: a campaign optimising for purchases and one optimising for leads are doing different jobs and cannot share a budget sensibly.",
      "Distinct markets with separate budgets are another legitimate case, particularly where currencies, languages or commercial priorities differ and one region must not absorb another's budget. So are products with very different margins, where a single blended cost target would misprice both.",
      "What does not justify a separate campaign is wanting to see results broken out. Reporting can be segmented after the fact by ad set, creative, placement, country or device without splitting delivery. Structure should be decided by what needs independent budget control, and reporting should be handled in reporting.",
    ],
  },
  {
    heading: "Consolidating an over-built account",
    body: [
      "Consolidation is disruptive because merging ad sets restarts learning, so it is worth doing deliberately rather than gradually. Identify the ad sets whose audiences overlap most and which individually sit below the event threshold, and merge them into a single ad set carrying the combined budget.",
      "Move the creative that performed best across the merged sets into the new one rather than starting from nothing. Creative history does not transfer, but a proven hook is still more likely to work than an untested one.",
      "Then allow an uninterrupted period — at least a week, and ideally until the ad set has cleared the event threshold — before judging the outcome. Consolidation reliably looks worse in the first few days, because what is being measured is the learning phase rather than the new structure.",
    ],
  },
];

const FAQS: GuideFaq[] = [
  {
    q: "How many ad sets should a Meta Ads account run?",
    a: "As few as the account's conversion volume allows. Each ad set needs roughly 50 optimisation events a week to stabilise, so dividing expected weekly conversions by 50 gives a realistic ceiling. An account producing 100 purchases a week can support about two purchase-optimised ad sets; running eight would leave all of them below the threshold.",
  },
  {
    q: "Does audience overlap actually raise costs?",
    a: "Yes. Ad sets targeting substantially overlapping audiences enter the same auctions for the same people, which raises costs without adding reach, and splits conversions so neither set stabilises. Interest-based audiences overlap far more than they appear to, because people interested in one topic are usually interested in adjacent ones.",
  },
  {
    q: "Is broad targeting better than detailed interest targeting?",
    a: "In accounts with reasonable conversion volume, usually yes. The delivery system is generally better at identifying likely converters than an advertiser is at describing them in advance, provided it has enough signal. Genuine constraints such as geography, language and regulated age limits still belong in targeting; predictions about who is most likely to buy generally do not.",
  },
  {
    q: "Should I separate campaigns by country?",
    a: "Only when the countries need independent budget control — for example where currencies, languages or commercial priorities differ, or where one market must not absorb another's budget. If they share an objective and a budget pool, separating them mainly divides conversion volume. Country-level results can be segmented in reporting without splitting delivery.",
  },
  {
    q: "How long after consolidating before I judge results?",
    a: "At least a week, and ideally until the consolidated ad set has cleared roughly 50 optimisation events. Merging ad sets restarts the learning phase, so early numbers reflect the exploration period rather than the new structure. Consolidation almost always looks worse for the first few days.",
  },
];

export const Route = createFileRoute("/guides/meta-ads-campaign-structure")({
  head: () =>
    guidePageHead({
      path: PATH,
      title: TITLE,
      description: DESCRIPTION,
      headline: HEADLINE,
      published: "2026-09-07",
      modified: "2026-09-07",
      faqs: FAQS,
    }),
  component: Page,
});

function Page() {
  return (
    <GuidePage
      eyebrow="Account structure"
      headline={HEADLINE}
      standfirst="Structure decides how much signal the delivery system gets to learn from. Most underperforming accounts are not badly targeted — they are split into more pieces than their conversion volume can support."
      keyPoints={KEY_POINTS}
      sections={SECTIONS}
      faqs={FAQS}
      updated="7 September 2026"
      readingMinutes={8}
      related={[
        { href: "/guides/meta-ads-learning-phase", label: "How the Meta Ads learning phase works" },
        {
          href: "/guides/why-meta-ad-costs-rise",
          label: "Why Meta ad costs rise while conversions fall",
        },
        { href: "/real-estate-lead-generation-meta-ads", label: "Meta advertising for property" },
      ]}
    />
  );
}
